import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

import { configurationAlternatePath } from "./configurationNavigation";

const ALTERNATE_MODAL_HELP =
  "Build a stock alternate at SGS (reason_not_in_batch = 'alternate'). Open the work page to clock in and configure " +
  "through SDTestApp. The configured device is added to inventory as an alternate at SGS.";

export function ConfigurationAlternateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const [alternateSku, setAlternateSku] = useState("");

  const alternatePath = useMemo(() => configurationAlternatePath(alternateSku), [alternateSku]);
  const alternateReady = alternateSku.trim() !== "";

  useEffect(() => {
    if (!open) {
      setAlternateSku("");
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="configuration-alternate-modal-title"
    >
      <div className="my-4 w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="configuration-alternate-modal-title" className="text-lg font-semibold text-slate-900">
              Configure alternate
            </h2>
            <HoverHintWithGlyph hoverHint={ALTERNATE_MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div className="space-y-2">
            <Label htmlFor="configuration-alternate-sku">Alternate SKU</Label>
            <Input
              id="configuration-alternate-sku"
              value={alternateSku}
              placeholder="e.g. S04TRA-X"
              autoFocus
              onChange={(event) => setAlternateSku(event.target.value)}
            />
          </div>
          {alternateReady ? (
            <Link
              to={alternatePath}
              className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Open configuration
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          ) : (
            <Button type="button" disabled>
              Open configuration
            </Button>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
